import helperFunctions from '../../utils/helperFunctions';
import { createLeadCustomerInsurancePayload } from './insurance';
import createLeadPaymentsData from './ecommerce';
import { YesNo } from '../../utils/enums';

export const getVehicleOfInterest = (business, route, vehicle) => {
  let vehicleOfInterest = {};
  const { VOIVinDecodeDisabled } = business.info.integrationSettings;
  if (
    VOIVinDecodeDisabled &&
    (route.query.autoverifyVehicleDetails === 'true' || route.query.DDCVehicleDetails === 'true')
  ) {
    vehicleOfInterest = {
      vin: route.query.vin,
      year: route.query.year,
      make: route.query.make,
      model: route.query.model,
      trim: route.query.trim,
      status: route.query.condition,
      price: route.query.purchasePrice,
      mileage: route.query.mileage,
    };
  } else {
    vehicleOfInterest = vehicle.vehicleInfo ? vehicle.vehicleInfo : {};
  }
  const category = route.query.category || vehicle.vehicleCategory;
  const stockNumber = route.query.stockNumber || null;
  if (category) {
    vehicleOfInterest.category = category;
  }
  if (stockNumber) {
    vehicleOfInterest.stockNumber = stockNumber;
  }
  return vehicleOfInterest;
};

export const prepareCreateLead = (business, route, lead, vehicle) => {
  const vehicleOfInterest = getVehicleOfInterest(business, route, vehicle);
  const leadBody = {
    subType: lead.subType,
    sourceUrl: route.query.referrer_url ? route.query.referrer_url : '',
    utm: lead.utm,
    googleClientId: lead.googleClientId,
    customDealerLeadData: lead.customDealerLeadData,
    coag: lead.coag,
    widgetId: route.query.widgetId || null,
    referralType: route.query.referralType || null,
    VOIVinDecodeDisabled: business.info.integrationSettings.VOIVinDecodeDisabled,
    vehicleOfInterest,
  };
  return Object.freeze(leadBody);
};

export const prepareUpdateLead = (
  user,
  vehicle,
  credit,
  trade,
  route,
  lead,
  experiment,
  paymentCalculator,
  testDrive,
  insurance,
  buildAndPrice,
  business
) => {
  // Ecom & B&P payments data
  const paymentsData = createLeadPaymentsData({
    route,
    trade,
    paymentCalculator,
  });

  const vehicleOfInterest = getVehicleOfInterest(business, route, vehicle);

  const updateLeadBody = {
    customer: {
      name: {
        firstName: user.firstName,
        lastName: user.lastName,
      },
      email: user.emailAddress,
      phone: user.phoneNumber || null,
      language: user.language,
      address: {
        address_line_1: user.streetAddress,
        address_line_2: user.unit,
        city: user.city || null,
        postal_code: user.postalCode || user.zipCode,
        province_id: user.provinceCode || user.stateCode,
        country_code: user.country,
      },
      mobilePhone: user.phoneNumber,
      payload: {
        ...createLeadCustomerInsurancePayload({
          user,
          insurance,
        }),
      },
    },
    languageCode: user.language,
    buildAndPriceVehicle: buildAndPrice.vehicleConfig
      ? {
          ...buildAndPrice.vehicleConfig.vehicle,
          serializedValue: buildAndPrice.vehicleConfig.serializedValue,
          isFullyConfigured: buildAndPrice.vehicleConfig.isFullyConfigured,
        }
      : null,
    creditLeadInfo: {
      creditRating: helperFunctions.creditGradeBiMap(credit.creditScore.creditGrade),
      creditRatingType: helperFunctions.creditRatingTypeMap(credit.bureauCode),
      vehiclePrice: paymentsData ? paymentsData.vehiclePrice : null,
      tradeInValue: paymentsData ? paymentsData.tradeInValue : null,
      downPayment: paymentsData ? paymentsData.downPayment : null,
      termInMonths: paymentsData ? paymentsData.termInMonths : null,
      salesTax: paymentsData ? paymentsData.salesTax : null,
      luxuryTax: paymentsData ? paymentsData.luxuryTax : null,
      freight: paymentsData ? paymentsData.freight : null,
      provincialFees: paymentsData ? paymentsData.provincialFees : null,
      interestRate: paymentsData ? paymentsData.interestRate : null,
      paymentAmount: paymentsData ? paymentsData.paymentAmount : null,
      tradeInOwing: paymentsData ? paymentsData.tradeInOwing : null,
      loanAmount: paymentsData ? paymentsData.loanAmount : null,
      payload: user.financeDetails,
      paymentFrequency: paymentsData ? paymentsData.paymentFrequency : null,
      regionalCreditRating: helperFunctions.creditGradeBiMap(credit.regionalCreditScore),
      paymentType: paymentsData ? paymentsData.paymentType : null,
      leaseInfo: paymentsData ? paymentsData.leaseInfo : null,
      incentives: paymentsData ? paymentsData.incentives : null,
      calculator: paymentsData ? paymentsData.calculator : null,
      hasCreditPlus: !!(credit.hasCreditPlusCreditResults || credit.hasEquifaxCreditResults),
      financeApplicationVehicle:
        credit.CIMFinanceApplicationVehicle &&
        credit.applyCIMFinanceApplicationVehicle &&
        credit.CIMFinanceApplicationVehicle.vehicle
          ? {
              year: credit.CIMFinanceApplicationVehicle.vehicle.year || null,
              make: credit.CIMFinanceApplicationVehicle.vehicle.make || null,
              model: credit.CIMFinanceApplicationVehicle.vehicle.model || null,
              trim: credit.CIMFinanceApplicationVehicle.vehicle.trim || null,
              style: credit.CIMFinanceApplicationVehicle.vehicle.style || null,
              vin: credit.CIMFinanceApplicationVehicle.vehicle.identifier || null,
              status: credit.CIMFinanceApplicationVehicle.vehicle.status || null,
              price: credit.CIMFinanceApplicationVehicle.vehicle.price || null,
              mileage: credit.CIMFinanceApplicationVehicle.vehicle.kilometers || null,
            }
          : null,
    },
    tradeLeadInfo: {
      vehicle: {
        year: trade.updateLeadVehicles.tradein.year || null,
        make: trade.updateLeadVehicles.tradein.make || null,
        model: trade.updateLeadVehicles.tradein.model || null,
        trim:
          trade.updateLeadVehicles.tradein.trim === 'no-trim' ? null : trade.updateLeadVehicles.tradein.trim || null,
        style: trade.updateLeadVehicles.tradein.style,
        mileage: trade.updateLeadVehicles.tradein.mileage,
        vin: trade.updateLeadVehicles.tradein.vin || null,
        additionalDetails: trade.updateLeadVehicles.tradein.additionalDetails || null,
        type: trade.updateLeadVehicles.tradein.type || null,
        drivetrain: trade.updateLeadVehicles.tradein.drivetrain || '',
        transmission: trade.updateLeadVehicles.tradein.transmission || '',
        bodyStyle: trade.updateLeadVehicles.tradein.bodyStyle || '',
        boxLength: trade.updateLeadVehicles.tradein.boxLength,
        engine: trade.updateLeadVehicles.tradein.engine || '',
        fuelType: trade.updateLeadVehicles.tradein.fuelType || '',
      },
      additionalVehicleAttributes: {
        colour: trade.updateLeadVehicles.tradein.additionalVehicleAttributes.colour || null,
        condition: trade.updateLeadVehicles.tradein.additionalVehicleAttributes.condition || null,
        beenInAccident:
          trade.updateLeadVehicles.tradein.additionalVehicleAttributes.beenInAccident === YesNo.YES
            ? true
            : trade.updateLeadVehicles.tradein.additionalVehicleAttributes.beenInAccident === YesNo.NO
            ? false
            : null,
        needsBodyWork:
          trade.updateLeadVehicles.tradein.additionalVehicleAttributes.needsBodyWork === YesNo.YES
            ? true
            : trade.updateLeadVehicles.tradein.additionalVehicleAttributes.needsBodyWork === YesNo.NO
            ? false
            : null,
        hasMechanicalIssues:
          trade.updateLeadVehicles.tradein.additionalVehicleAttributes.hasMechanicalIssues === YesNo.YES
            ? true
            : trade.updateLeadVehicles.tradein.additionalVehicleAttributes.hasMechanicalIssues === YesNo.NO
            ? false
            : null,
      },
      tradeEquityUsedForCIM: trade.updateLeadVehicles.tradein.tradeEquityUsedForCIM,
      high_value: trade.tradein.trade_in_value.high_value || null,
      low_value: trade.tradein.trade_in_value.low_value || null,
      deductions: null,
      customer_referral_url: null,
      low_list: trade.tradein.low_list_price || null,
      high_list: trade.tradein.high_list_price || null,
      list_count: trade.tradein.vehicle_count || null,
      aged_discount: trade.tradein.deductions.aged_discount || null,
      reconditioning: trade.tradein.deductions.reconditioning || null,
      advertising: trade.tradein.deductions.advertising || null,
      overhead: trade.tradein.deductions.overhead || null,
      dealer_profit: trade.tradein.deductions.dealer_profit || null,
      applyTradeIn: paymentsData ? paymentsData.applyTradeIn : null,
      equityAssessmentBalance: trade.updateLeadVehicles.tradein.balanceOwing,
    },
    tradeLineForEquityAssessment: credit.selectedEquifaxTradeLine.tradeLineId ? credit.selectedEquifaxTradeLine : null,
    insuranceLeadInfo: {
      referenceId: lead.leadId,
      referrerUrl: route.query.referrer_url ? route.query.referrer_url : '',
      quotePayload: insurance.responseData ? [insurance.responseData] : [],
      purchasePrice: insurance.insurance.price || null,
      vehicle: {
        year: insurance.updateLeadVehicles.insurance.year || null,
        make: insurance.updateLeadVehicles.insurance.make || null,
        model: insurance.updateLeadVehicles.insurance.model || null,
        trim: null,
        style: null,
        mileage: null,
        vin: route.query.vin || null,
        status: route.query.condition || null,
      },
    },
    ecommerceLeadInfo: {
      reserved: null,
    },
    birthdate: helperFunctions.birthdateShaper(user.birthdate),
    sourceUrl: route.query.referrer_url ? route.query.referrer_url : '',
    experiment,
    oemOfInterest: user.oemOfInterest,
    testDriveLeadInfo: {
      requestedDate: testDrive.driveDate || null,
      timeOfDayPreference: testDrive.chosenTimeOfDay || null,
      language: testDrive.chosenLanguage || null,
      genderPreference: testDrive.chosenGender || null,
      technologyInterests: testDrive.importantFeatures || null,
      routeName: testDrive.chosenRoute || null,
      beverage: testDrive.chosenBeverage || null,
      comments: testDrive.comments || null,
    },
    acquireLeadInfo: {
      vehicle: {
        year: trade.updateLeadVehicles.acquire.year || null,
        make: trade.updateLeadVehicles.acquire.make || null,
        model: trade.updateLeadVehicles.acquire.model || null,
        trim:
          trade.updateLeadVehicles.acquire.trim === 'no-trim' ? null : trade.updateLeadVehicles.acquire.trim || null,
        style: trade.updateLeadVehicles.acquire.style,
        mileage: trade.updateLeadVehicles.acquire.mileage,
        vin: trade.updateLeadVehicles.acquire.vin || null,
        additionalDetails: trade.updateLeadVehicles.acquire.additionalDetails || null,
        drivetrain: trade.updateLeadVehicles.acquire.drivetrain || '',
        transmission: trade.updateLeadVehicles.acquire.transmission || '',
        bodyStyle: trade.updateLeadVehicles.acquire.bodyStyle || '',
        boxLength: trade.updateLeadVehicles.acquire.boxLength,
        engine: trade.updateLeadVehicles.acquire.engine || '',
        fuelType: trade.updateLeadVehicles.acquire.fuelType || '',
      },
      additionalVehicleAttributes: {
        colour: trade.updateLeadVehicles.acquire.additionalVehicleAttributes.colour || null,
        condition: trade.updateLeadVehicles.acquire.additionalVehicleAttributes.condition || null,
        beenInAccident:
          trade.updateLeadVehicles.acquire.additionalVehicleAttributes.beenInAccident === YesNo.YES
            ? true
            : trade.updateLeadVehicles.acquire.additionalVehicleAttributes.beenInAccident === YesNo.NO
            ? false
            : null,
        needsBodyWork:
          trade.updateLeadVehicles.acquire.additionalVehicleAttributes.needsBodyWork === YesNo.YES
            ? true
            : trade.updateLeadVehicles.acquire.additionalVehicleAttributes.needsBodyWork === YesNo.NO
            ? false
            : null,
        hasMechanicalIssues:
          trade.updateLeadVehicles.acquire.additionalVehicleAttributes.hasMechanicalIssues === YesNo.YES
            ? true
            : trade.updateLeadVehicles.acquire.additionalVehicleAttributes.hasMechanicalIssues === YesNo.NO
            ? false
            : null,
      },
      high_value: trade.acquire.buy_value.high_value || null,
      low_value: trade.acquire.buy_value.low_value || null,
      customer_referral_url: route.query.referrer_url ? route.query.referrer_url : null,
      list_count: trade.acquire.vehicle_count || null,
    },
    vehicleOfInterest,
    tradeInSource: trade.tradeInSource || null,
  };
  return Object.freeze(updateLeadBody);
};

export const prepareCreateConsumerId = (user, business) => {
  const createConsumerBody = {
    apiKey: business.api.key,
    externalId: business.api.externalID,
    name: {
      firstName: user.firstName,
      lastName: user.lastName,
    },
    email: user.emailAddress,
    phone: user.phoneNumber,
    address: {
      address_line_1: user.streetAddress,
      address_line_2: user.unit,
      city: user.city || null,
      postal_code: user.postalCode || user.zipCode,
      province_id: user.provinceCode || user.stateCode,
      country_code: user.country,
    },
  };
  return Object.freeze(createConsumerBody);
};

export const prepareCreateFinanceApplication = user => {
  const createFinanceApplicationBody = {
    employer: {
      name: user.financeApplication.employerName,
      phoneNumber: user.financeApplication.employerNumber,
      address: {
        streetNumber: user.financeApplication.streetNumber,
        streetName: user.financeApplication.streetName,
        suiteNumber: user.financeApplication.suiteNumber,
        city: user.financeApplication.city,
        provinceCode: user.financeApplication.provinceCode,
        countryCode: user.financeApplication.country,
        postalCode: user.financeApplication.postalCode,
      },
    },
    currentOccupation: user.financeApplication.currentOccupation,
    annualGrossIncome: user.financeDetails.grossIncome,
    yearsWorked: user.financeApplication.yearsWorked,
    home: {
      ownershipType: user.financeApplication.ownershipType,
      mortgageCompanyOrLandlord: user.financeApplication.mortgageCompany,
      monthlyPayment: user.financeDetails.monthlyPayment,
    },
  };
  return Object.freeze(createFinanceApplicationBody);
};
